import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/format";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export function StoreProvider({ children }) {
  const [currency, setCurrency] = useState(() => localStorage.getItem("jk_currency") || "INR");
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jk_cart") || "[]"); } catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jk_wishlist") || "[]"); } catch { return []; }
  });
  const [promo, setPromo] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [config, setConfig] = useState({
    usd_rate: 83, free_shipping_threshold: 2999, shipping_fee: 99, express_fee: 199,
    razorpay_enabled: false, whatsapp_number: "919960705886", email: "justkords@gmail.com",
    socials: { instagram: "https://instagram.com/justkords", facebook: "https://facebook.com/justkords", whatsapp: "https://wa.me/919960705886" },
  });

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const [user, setUser] = useState(undefined); // undefined = still checking, null = signed out
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // AuthCallback exchanges the session first
    axios.get(`${API}/auth/me`, { withCredentials: true }).then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);
  const [wallet, setWallet] = useState(null);
  const refreshWallet = useCallback(() => {
    if (!user) { setWallet(null); return; }
    axios.get(`${API}/auth/wallet`, { withCredentials: true })
      .then((r) => setWallet(r.data))
      .catch(() => {});
  }, [user]);
  useEffect(() => { refreshWallet(); }, [refreshWallet]);
  useEffect(() => {
    if (user) syncWishlist();
  }, [user]);
  useEffect(() => { localStorage.setItem("jk_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("jk_currency", currency); }, [currency]);
  useEffect(() => { localStorage.setItem("jk_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  const addToCart = useCallback((product, size, qty = 1) => {
    setCart((c) => {
      const variant = (product.sizes || []).find((v) => v.size === size);
      const price_inr = variant && variant.price_inr != null ? variant.price_inr : product.price_inr;
      const price_usd = variant && variant.price_usd != null ? variant.price_usd : product.price_usd;
      const i = c.findIndex((x) => x.slug === product.slug && x.size === size);
      if (i > -1) {
        const n = [...c];
        n[i] = { ...n[i], qty: Math.min(n[i].qty + qty, 5) };
        return n;
      }
      return [...c, {
        slug: product.slug, name: product.name, size, qty,
        price_inr, price_usd,
        image: (product.images && product.images[0]) || "",
      }];
    });
  }, []);

  const updateQty = (slug, size, qty) => {
    setCart((c) => (qty <= 0
      ? c.filter((x) => !(x.slug === slug && x.size === size))
      : c.map((x) => (x.slug === slug && x.size === size ? { ...x, qty: Math.min(qty, 5) } : x))));
  };
  const removeFromCart = (slug, size) => setCart((c) => c.filter((x) => !(x.slug === slug && x.size === size)));
  const clearCart = () => setCart([]);
  const syncWishlist = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/wishlist`, { withCredentials: true });
      const local = JSON.parse(localStorage.getItem("jk_wishlist") || "[]");
      const merged = Array.from(new Set([...(data.slugs || []), ...local]));
      setWishlist(merged);
      localStorage.setItem("jk_wishlist", JSON.stringify(merged));
      if (JSON.stringify(local) !== JSON.stringify(merged)) {
        await axios.post(`${API}/auth/wishlist`, { slugs: merged }, { withCredentials: true });
      }
    } catch { /* offline-safe */ }
  };

  const toggleWishlist = (slug) => {
    setWishlist((w) => {
      const next = w.includes(slug) ? w.filter((s) => s !== slug) : [...w, slug];
      localStorage.setItem("jk_wishlist", JSON.stringify(next));
      if (user) axios.post(`${API}/auth/wishlist`, { slugs: next }, { withCredentials: true }).catch(() => {});
      return next;
    });
  };
  const applyPromo = (code) => {
    const ok = (code || "").trim().toUpperCase() === "JUST10";
    setPromo(ok ? "JUST10" : null);
    return ok;
  };

  const subtotalInr = useMemo(() => cart.reduce((s, x) => s + x.price_inr * x.qty, 0), [cart]);
  const subtotalUsd = useMemo(() => cart.reduce((s, x) => s + x.price_usd * x.qty, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, x) => s + x.qty, 0), [cart]);
  const discountInr = promo ? Math.round(subtotalInr * 0.1) : 0;
  const discountUsd = promo ? Math.round(subtotalUsd * 10) / 100 : 0;

  const fmt = (priceInr, priceUsd) => {
    if (currency === "USD") {
      const v = priceUsd != null ? priceUsd : Number(priceInr || 0) / config.usd_rate;
      return `$${Number(v).toFixed(2)}`;
    }
    return `₹${Number(priceInr || 0).toLocaleString("en-IN")}`;
  };

  const value = {
    currency, setCurrency, cart, addToCart, updateQty, removeFromCart, clearCart,
    cartCount, subtotalInr, subtotalUsd, discountInr, discountUsd,
    promo, applyPromo, wishlist, toggleWishlist,
    drawerOpen, setDrawerOpen, config, fmt, user, setUser, wallet, refreshWallet,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
